import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import Breadcrumbs from '@/components/Breadcrumbs';
import Reveal from '@/components/Reveal';
import CTAPanel from '@/components/CTAPanel';
import { EnquireButton, SiteVisitButton, WhatsAppButton, CallButton } from '@/components/CTAButtons';
import { IMAGES, PRICING } from '@/data/siteData';

const CONFIG_CONTENT = {
  '1-bhk': {
    name: '1 BHK',
    title: 'Provident Palm Vista 1 BHK | Shilphata',
    description: 'Provident Palm Vista 1 BHK homes in Shilphata — 406 & 453 sq.ft. carpet with balconies, from ₹52L all-inclusive. 16-acre tropical community on Kalyan-Shilphata Road.',
    h1: 'Provident Palm Vista 1 BHK – Smart Homes with Balconies',
    image: IMAGES.balcony,
    intro: 'The 1 BHK residences at Provident Palm Vista are designed for buyers who want a branded, amenity-rich address without stretching the budget. Available in two carpet-area options — 406 sq.ft. and 453 sq.ft. — every 1 BHK includes a balcony and full access to the 16-acre tropical ecosystem.',
    points: [
      { h: 'Who it suits', p: 'First-time homeowners, young professionals working in Thane, Navi Mumbai or the Kalyan belt, and investors seeking a rentable branded asset in the Shilphata corridor.' },
      { h: 'Lifestyle advantages', p: 'A 1 BHK here is not a compromise. Residents share the same 60+ amenities as every larger home — swimming pool, jogging track, cafe deck, Wi-Fi lounge and the full landscape of 30 palm varieties.' },
      { h: 'A balcony, always', p: 'Each 1 BHK is planned with a balcony, extending the living space outdoors — a rarity at this configuration in the micro-market.' },
      { h: 'Location advantage', p: 'Diva Station is about 3 minutes away, Thane–Belapur Road about 15 minutes, and Mindspace Airoli about 20 minutes — keeping the daily commute practical in every direction.' },
    ],
  },
  '2-bhk': {
    name: '2 BHK',
    title: 'Provident Palm Vista 2 BHK | Shilphata',
    description: 'Provident Palm Vista 2 BHK homes in Shilphata — 637 sq.ft. carpet family homes with balconies, from ₹83L all-inclusive. 70% open spaces, 60+ amenities.',
    h1: 'Provident Palm Vista 2 BHK – Room for the Whole Family',
    image: IMAGES.pool,
    intro: 'The 2 BHK residences at Provident Palm Vista are the project’s family sweet spot — a 637 sq.ft. carpet layout with a balcony, inside a 16-acre community where 70% of the land stays open and every generation has its own amenity zone.',
    points: [
      { h: 'Family suitability', p: 'A bedroom each for parents and children, a living room that hosts the extended family, and a balcony for morning tea — planned for the way Indian families actually live.' },
      { h: 'Lifestyle for every age', p: 'Kids get a play area, climbing wall, cricket nets and their own pool. Adults get a swimming pool, multi-play court and cafe deck. Grandparents get senior decks, lawns and reading coves.' },
      { h: 'A balcony, always', p: 'Every 2 BHK includes a balcony overlooking the tropical landscape — your private slice of the 70% open spaces.' },
      { h: 'Location advantage', p: 'Schools like Guardians School are ~2 minutes away, hospitals like MGM ~3 minutes, and business hubs like Millennium Business Park ~12 minutes — daily life stays close to home.' },
    ],
  },
  '2-5-bhk': {
    name: '2.5 BHK',
    title: 'Provident Palm Vista 2.5 BHK | Shilphata',
    description: 'Provident Palm Vista 2.5 BHK homes in Shilphata — 811 sq.ft. carpet with a flexible extra room and balcony, from ₹1.12 Cr all-inclusive.',
    h1: 'Provident Palm Vista 2.5 BHK – Space That Grows With You',
    image: IMAGES.clubhouse,
    intro: 'The 2.5 BHK residences at Provident Palm Vista are the most expansive homes in the development — an 811 sq.ft. carpet layout with an additional flexible room and a balcony, for families who need space to evolve.',
    points: [
      { h: 'The extra half room', p: 'A study, a kids’ room, a home office, a puja room or a guest space — the additional room adapts as your family’s needs change over the years.' },
      { h: 'Larger family requirements', p: 'For joint families, work-from-home couples or parents planning ahead, the 2.5 BHK removes the pressure to upgrade within a few years.' },
      { h: 'Lifestyle & amenities', p: 'The same 60+ amenity ecosystem, with the added comfort of coming home to more space — plus the clubhouse, decks and gardens of the 16-acre landscape.' },
      { h: 'Location advantage', p: 'About 5 minutes from Shilphata Junction, with Thane, Navi Mumbai and Kalyan–Dombivli all within a practical commute band.' },
    ],
  },
};

const ConfigPage = ({ configKey }) => {
  const c = CONFIG_CONTENT[configKey];
  if (!c) return <Navigate to="/" replace />;
  const pricing = PRICING.filter((p) => p.type === c.name);
  const crumb = [{ name: c.name }];
  return (
    <>
      <Seo title={c.title} description={c.description} path={`/${configKey}`}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: c.name, path: `/${configKey}` }])]} />
      <section className="pt-28 md:pt-36 pb-16 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={crumb} />
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">{c.name} Residences</p>
              <h1 className="font-display text-4xl sm:text-5xl font-medium text-palm mt-3 leading-[1.08] tracking-tight">{c.h1}</h1>
              <p className="text-[15px] text-ink/75 mt-5 leading-relaxed">{c.intro}</p>
              <div className="flex flex-wrap gap-3 mt-7">
                <EnquireButton testid={`${configKey}-price-btn`} />
                <WhatsAppButton testid={`${configKey}-whatsapp-btn`} />
                <CallButton testid={`${configKey}-call-btn`} />
                <button type="button" data-testid={`${configKey}-site-visit-btn`}
                  onClick={() => window.dispatchEvent(new CustomEvent('open-enquiry', { detail: { source: 'site_visit' } }))}
                  className="inline-flex items-center gap-2 rounded-full border border-palm/25 text-palm px-6 py-3 text-sm font-semibold hover:border-gold hover:text-gold transition-all">
                  Book Site Visit
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <img src={c.image} alt={`Provident Palm Vista ${c.name} lifestyle`} className="rounded-3xl w-full object-cover aspect-[4/3] shadow-2xl" loading="lazy" width={1200} height={900} />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-paper grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid sm:grid-cols-2 gap-5">
          {c.points.map((pt, i) => (
            <Reveal key={pt.h} delay={i * 0.06}>
              <div className="bg-white border border-line rounded-2xl p-7 h-full">
                <div className="flex items-center gap-2.5">
                  <span className="h-7 w-7 rounded-full bg-palm flex items-center justify-center"><Check className="h-3.5 w-3.5 text-gold" /></span>
                  <h2 className="font-semibold text-palm">{pt.h}</h2>
                </div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{pt.p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-medium text-palm">{c.name} pricing at a glance</h2>
            <p className="text-sm text-muted-foreground mt-2">Latest all-inclusive cost sheet. Prices include a mandatory car parking; a spot offer applies on same-day booking.</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-6 overflow-x-auto rounded-2xl border border-line bg-white shadow-sm">
            <table className="w-full text-sm" data-testid={`${configKey}-pricing-table`}>
              <thead>
                <tr className="bg-palm text-ivory text-left">
                  <th className="px-5 py-3.5 font-semibold">Type</th>
                  <th className="px-5 py-3.5 font-semibold">Carpet Area</th>
                  <th className="px-5 py-3.5 font-semibold">Floors 1–10</th>
                  <th className="px-5 py-3.5 font-semibold">Floors 11–20</th>
                  <th className="px-5 py-3.5 font-semibold">Floors 21–30</th>
                </tr>
              </thead>
              <tbody>
                {pricing.map((p, i) => (
                  <tr key={i} className="border-t border-line">
                    <td className="px-5 py-3.5 font-semibold text-palm">{p.type}</td>
                    <td className="px-5 py-3.5">{p.carpet}</td>
                    <td className="px-5 py-3.5 text-gold font-semibold">{p.f1}</td>
                    <td className="px-5 py-3.5">{p.f2}</td>
                    <td className="px-5 py-3.5">{p.f3}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
          <Reveal delay={0.15} className="mt-8 flex flex-wrap gap-5">
            <Link to="/provident-palm-vista-floor-plan" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">View {c.name} floor plans <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/provident-palm-vista-amenities" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Explore amenities <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/provident-palm-vista-location" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">See location <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Contact us <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>
      <CTAPanel title={`Get ${c.name} Price & Floor Plan`} />
    </>
  );
};

export default ConfigPage;
